import { computed, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';

import { Sentry } from '@shared/sentry';
import { Analytics } from '@shared/analytics';
import { vueQueryLib } from '@shared/lib/vue-query-lib';
import type { DateLib } from '@shared/lib';

import { DatesPickerApi } from '../api';
import { analyticsEvents } from '../config';

type NightsQueryProps = {
  countryToId: Ref<number>;
  townFromId: Ref<number>;
  dateFrom: Ref<DateLib.DateProp>;
  dateTo: Ref<DateLib.DateProp>;
};

export const useNightsQuery = ({
  countryToId,
  townFromId,
  dateFrom,
  dateTo,
}: NightsQueryProps) => {
  /**
   * @description since all params are required, we need to check only for date params,
   * since they can be passed as an empty string
   */
  const isQueryEnabled = computed(
    () => dateFrom.value !== '' && dateTo.value !== '',
  );

  const nightsQuery = useQuery({
    queryKey: ['nights-query', countryToId, townFromId, dateFrom, dateTo],
    queryFn: async () => {
      const response = await DatesPickerApi.getNightsCount(
        countryToId.value,
        townFromId.value,
        dateFrom.value,
        dateTo.value,
      );
      return response.data;
    },
    staleTime: Infinity, // Static data from admin, so it can't become obsolete during a user session
    enabled: isQueryEnabled,
    refetchOnWindowFocus: false,
  });

  vueQueryLib.withOnSuccess({
    query: nightsQuery,
    onSuccess() {
      Analytics.logEvent({
        event: analyticsEvents.NIGHTS_SUCCESS,
      });
    },
  });

  vueQueryLib.withOnError({
    query: nightsQuery,
    onError(error) {
      Analytics.logEvent({
        event: analyticsEvents.NIGHTS_ERROR,
      });

      Sentry.captureExWithCause('ERR_FAILED_TO_FETCH_NIGHTS', {
        cause: error,
      });
    },
  });

  return nightsQuery;
};
